import {
    BasicDetailReducerState,
    BasicReducerState,
    initialReducerState,
    TotalsListReducerStateType
} from "../BasicReducerState";
import {OutgoingInvoice} from "../../models/accounting/OutgoingInvoice";
import {
    CREATE_OUTGOING_INVOICE,
    CREATE_OUTGOING_INVOICE_ERROR,
    CREATE_OUTGOING_INVOICE_SUCCESS,
    DELETE_OUTGOING_INVOICE_BY_ID,
    DELETE_OUTGOING_INVOICE_BY_ID_ERROR,
    DELETE_OUTGOING_INVOICE_BY_ID_SUCCESS,
    FETCH_OUTGOING_INVOICE_BY_ID,
    FETCH_OUTGOING_INVOICE_BY_ID_ERROR,
    FETCH_OUTGOING_INVOICE_BY_ID_SUCCESS,
    FETCH_OUTGOING_INVOICE_FILES_BY_ID,
    FETCH_OUTGOING_INVOICE_FILES_BY_ID_ERROR,
    FETCH_OUTGOING_INVOICE_FILES_BY_ID_SUCCESS,
    FETCH_OUTGOING_INVOICES,
    FETCH_OUTGOING_INVOICES_CHECK_FINAL,
    FETCH_OUTGOING_INVOICES_CHECK_FINAL_ERROR,
    FETCH_OUTGOING_INVOICES_CHECK_FINAL_SUCCESS,
    FETCH_OUTGOING_INVOICES_ERROR,
    FETCH_OUTGOING_INVOICES_NEXT_SERIAL,
    FETCH_OUTGOING_INVOICES_NEXT_SERIAL_ERROR,
    FETCH_OUTGOING_INVOICES_NEXT_SERIAL_SUCCESS,
    FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT,
    FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR,
    FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS,
    FETCH_OUTGOING_INVOICES_SUCCESS,
    MARK_CANCELED_OUTGOING_INVOICE,
    MARK_CANCELED_OUTGOING_INVOICE_ERROR,
    MARK_CANCELED_OUTGOING_INVOICE_SUCCESS,
    MARK_FINALIZED_OUTGOING_INVOICE,
    MARK_FINALIZED_OUTGOING_INVOICE_ERROR,
    MARK_FINALIZED_OUTGOING_INVOICE_SUCCESS,
    OutgoingInvoiceViewActionTypes,
    UPDATE_OUTGOING_INVOICE,
    UPDATE_OUTGOING_INVOICE_ERROR,
    UPDATE_OUTGOING_INVOICE_SUCCESS
} from "../../actions/account-book/AccountBookActions";
import {
    DELETE_OUTGOING_INVOICE_PAYMENT,
    DELETE_OUTGOING_INVOICE_PAYMENT_ERROR,
    DELETE_OUTGOING_INVOICE_PAYMENT_SUCCESS,
    INSERT_OUTGOING_INVOICE_PAYMENT,
    INSERT_OUTGOING_INVOICE_PAYMENT_ERROR,
    INSERT_OUTGOING_INVOICE_PAYMENT_SUCCESS,
} from "../../actions/account-book/OutgoingInvoicePaymentActions"
import {Nullable} from "../../models/common/Nullable";
import {
    errorFetchingRequestState,
    getInitialRequestState,
    RequestState,
    startFetchingRequestState,
    successFetchingRequestState
} from "../../models/common/RequestState";
import {Money} from "../../models/accounting/Finance";
import {makeSensitive, Sensitive} from "../../models/common/Sensitive";
import {Identifier, makeNumericId} from "../../models/common/Identifier";
import {FileData} from "../../models/common/FileData";
import {BillSummable} from "../../models/accounting/AccountBook";


export interface OutgoingInvoiceState extends TotalsListReducerStateType< Nullable<OutgoingInvoice[]> , BillSummable> {
    nextSerialState: string
    nextSerialRequest: RequestState
    totals: BillSummable
    prePaymentByProject: {
        projectId: Identifier<number>
        prePayment: Nullable< Sensitive< Money > >,
        request: RequestState
    }
    payment: {
        changingRequest: RequestState
    }
    files: {
        entries: { [ key: number ]: FileData[] }
        request: RequestState
    }
    checkFinalInvoice: {
        projectId: Identifier<number>
        finalInvoiceCount: number
        request: RequestState
    }
    detailedEntries: BasicDetailReducerState< Nullable< OutgoingInvoice > >,
    changingRequest: RequestState
}

const initialState: OutgoingInvoiceState = {
    ...initialReducerState(),
    nextSerialState: "0",
    nextSerialRequest: getInitialRequestState(),
    prePaymentByProject: {
        projectId: makeNumericId( 0),
        prePayment: null,
        request: getInitialRequestState()
    },
    files: {
        request: getInitialRequestState(),
        entries: {}
    },
    totals: {
        amountOwed: makeSensitive( 0 ),
        amountOwedWithTax: makeSensitive( 0 ),
        finalAmountOwed: makeSensitive( 0 ),
        totalPayments: makeSensitive( 0 ),
        salesTaxByTax: {},
        openAmountOwed: makeSensitive( 0 ),
        totalSalesTaxAmount: makeSensitive( 0 )
    },
    payment: {
        changingRequest: getInitialRequestState()
    },
    checkFinalInvoice: {
        projectId: makeNumericId( 0 ),
        finalInvoiceCount: 0,
        request: getInitialRequestState()
    }
};

export function OutgoingInvoiceReducer(
    state: OutgoingInvoiceState = initialState,
    action: OutgoingInvoiceViewActionTypes
) {

    switch( action.type ) {
        case FETCH_OUTGOING_INVOICES:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: startFetchingRequestState()
                }
            };
        case FETCH_OUTGOING_INVOICES_SUCCESS:
            return {
                ...state,
                totals: action.payload.totals,
                totalEntries: action.payload.totalEntries,
                entries: {
                    current: action.payload.entries,
                    request: successFetchingRequestState()
                }
            };
        case FETCH_OUTGOING_INVOICES_ERROR:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: errorFetchingRequestState( action.error )
                }
            };
        case FETCH_OUTGOING_INVOICES_NEXT_SERIAL:
            return {
                ...state,
                nextSerialState: "0",
                nextSerialRequest: startFetchingRequestState()
            };
        case FETCH_OUTGOING_INVOICES_NEXT_SERIAL_SUCCESS:
            return {
                ...state,
                nextSerialState: action.payload,
                nextSerialRequest: successFetchingRequestState()
            };
        case FETCH_OUTGOING_INVOICES_NEXT_SERIAL_ERROR:
            return {
                ...state,
                nextSerialState: "",
                nextSerialRequest: errorFetchingRequestState( action.error )
            };

        case FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT:
            return {
                ...state,
                prePaymentByProject: {
                    ...state.prePaymentByProject,
                    projectId: action.projectId,
                    request: startFetchingRequestState()
                }
            };
        case FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS:
            return {
                ...state,
                prePaymentByProject: {
                    ...state.prePaymentByProject,
                    prePayment: action.payload,
                    request: successFetchingRequestState()
                }
            };
        case FETCH_OUTGOING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR:
            return {
                ...state,
                prePaymentByProject: {
                    ...state.prePaymentByProject,
                    request: errorFetchingRequestState( action.error )
                }
            };
        case FETCH_OUTGOING_INVOICES_CHECK_FINAL:
            return {
                ...state,
                checkFinalInvoice: {
                    finalInvoiceCount: 0,
                    projectId: action.projectId,
                    request: startFetchingRequestState()
                }
            };
        case FETCH_OUTGOING_INVOICES_CHECK_FINAL_SUCCESS:
            return {
                ...state,
                checkFinalInvoice: {
                    ...state.checkFinalInvoice,
                    finalInvoiceCount: action.payload,
                    request: successFetchingRequestState()
                }
            };
        case FETCH_OUTGOING_INVOICES_CHECK_FINAL_ERROR:
            return {
                ...state,
                checkFinalInvoice: {
                    ...state.checkFinalInvoice,
                    finalInvoiceCount: 0,
                    request: errorFetchingRequestState( action.error )
                }
            };
        case CREATE_OUTGOING_INVOICE:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case CREATE_OUTGOING_INVOICE_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case CREATE_OUTGOING_INVOICE_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case FETCH_OUTGOING_INVOICE_BY_ID:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [ action.outgoingInvoiceId.rawValue ]: {
                        entry: null,
                        request: startFetchingRequestState()
                    }
                }
            };
        case FETCH_OUTGOING_INVOICE_BY_ID_SUCCESS:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [ action.payload.outgoingInvoiceId.rawValue ]: {
                        entry: action.payload,
                        request: successFetchingRequestState()
                    }
                }
            };
        case FETCH_OUTGOING_INVOICE_BY_ID_ERROR:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [ action.outgoingInvoiceId.rawValue ]: {
                        entry: null,
                        request: errorFetchingRequestState( action.error )
                    }
                }
            };
        case MARK_CANCELED_OUTGOING_INVOICE:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case MARK_CANCELED_OUTGOING_INVOICE_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case MARK_CANCELED_OUTGOING_INVOICE_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case MARK_FINALIZED_OUTGOING_INVOICE:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case MARK_FINALIZED_OUTGOING_INVOICE_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case MARK_FINALIZED_OUTGOING_INVOICE_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case INSERT_OUTGOING_INVOICE_PAYMENT:
            return {
                ...state,
                payment: {
                    changingRequest: startFetchingRequestState()
                }
            };
        case INSERT_OUTGOING_INVOICE_PAYMENT_SUCCESS:
            return {
                ...state,
                payment: {
                    changingRequest: successFetchingRequestState()
                }
            };
        case INSERT_OUTGOING_INVOICE_PAYMENT_ERROR:
            return {
                ...state,
                payment: {
                    changingRequest: errorFetchingRequestState( action.error )
                }
            };
        case DELETE_OUTGOING_INVOICE_PAYMENT:
            return {
                ...state,
                payment: {
                    changingRequest: startFetchingRequestState()
                }
            };
        case DELETE_OUTGOING_INVOICE_PAYMENT_SUCCESS:
            return {
                ...state,
                payment: {
                    changingRequest: successFetchingRequestState()
                }
            };
        case DELETE_OUTGOING_INVOICE_PAYMENT_ERROR:
            return {
                ...state,
                payment: {
                    changingRequest: errorFetchingRequestState( action.error )
                }
            };
        case FETCH_OUTGOING_INVOICE_FILES_BY_ID:
            return {
                ...state,
                files: {
                    request: startFetchingRequestState(),
                    entries: { [ action.outgoingInvoiceId.rawValue]: [] }
                }
            };
        case FETCH_OUTGOING_INVOICE_FILES_BY_ID_SUCCESS:
            return {
                ...state,
                files: {
                    request: successFetchingRequestState(),
                    entries: { [ action.outgoingInvoiceId.rawValue ]: action.payload }
                }
            };
        case FETCH_OUTGOING_INVOICE_FILES_BY_ID_ERROR:
            return {
                ...state,
                files: {
                    request: errorFetchingRequestState( action.error )
                }
            };
        case UPDATE_OUTGOING_INVOICE:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case DELETE_OUTGOING_INVOICE_BY_ID:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case UPDATE_OUTGOING_INVOICE_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case DELETE_OUTGOING_INVOICE_BY_ID_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case UPDATE_OUTGOING_INVOICE_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case DELETE_OUTGOING_INVOICE_BY_ID_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        default:
            return state;
    }
}